import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  Calendar,
  ExternalLink,
  Linkedin,
  Globe,
  Edit,
  Trash2,
  MessageSquare,
  Send,
} from 'lucide-react'
import { candidatesApi, api } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { getInitials, formatDate, formatRelativeTime } from '@/lib/utils'

const stageColors: Record<string, string> = {
  sourced: '#6b7280',
  applied: '#3b82f6',
  screening: '#8b5cf6',
  interview: '#f59e0b',
  offer: '#10b981',
  hired: '#059669',
  rejected: '#ef4444',
}

export function CandidateDetailPage() {
  const { candidateId } = useParams({ from: '/_app/candidates/$candidateId' })
  const [activeTab, setActiveTab] = useState('overview')
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [noteContent, setNoteContent] = useState('')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: candidate, isLoading } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => candidatesApi.get(candidateId),
  })

  const { data: timeline } = useQuery({
    queryKey: ['candidate-timeline', candidateId],
    queryFn: () => api.get<any[]>(`/candidates/${candidateId}/timeline`),
    enabled: !!candidate,
  })

  const deleteCandidate = useMutation({
    mutationFn: () => candidatesApi.delete(candidateId),
    onSuccess: () => {
      toast({ title: 'Candidate deleted', variant: 'success' })
      navigate({ to: '/candidates' })
    },
    onError: (err: any) => {
      toast({ title: 'Failed to delete', description: err.message, variant: 'destructive' })
    },
  })

  const addNote = useMutation({
    mutationFn: (content: string) => api.post(`/notes`, { candidateId, content }),
    onSuccess: () => {
      setNoteContent('')
      queryClient.invalidateQueries({ queryKey: ['candidate-timeline', candidateId] })
      toast({ title: 'Note added', variant: 'success' })
    },
    onError: (err: any) => {
      toast({ title: 'Failed to add note', description: err.message, variant: 'destructive' })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    )
  }

  if (!candidate) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Candidate not found</h2>
        <Button variant="link" asChild className="mt-4">
          <Link to="/candidates">Back to candidates</Link>
        </Button>
      </div>
    )
  }

  const c = candidate as any

  return (
    <PageLayout fullHeight>
      <div className="h-full flex flex-col overflow-y-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/candidates">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <Avatar className="h-14 w-14">
            <AvatarFallback className="text-lg">
              {getInitials(`${c.firstName} ${c.lastName}`)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{c.firstName} {c.lastName}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              {c.currentTitle && (
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5" />
                  {c.currentTitle}
                  {c.currentCompany && ` at ${c.currentCompany}`}
                </span>
              )}
              {c.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {c.location}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" className="text-destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="applications">Applications</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            {/* Contact Info */}
            <div className="md:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <a href={`mailto:${c.email}`} className="text-sm font-medium hover:underline">
                          {c.email}
                        </a>
                      </div>
                    </div>
                    {c.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <a href={`tel:${c.phone}`} className="text-sm font-medium hover:underline">
                            {c.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {c.location && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Location</p>
                          <p className="text-sm font-medium">{c.location}</p>
                        </div>
                      </div>
                    )}
                    {c.linkedinUrl && (
                      <div className="flex items-center gap-3">
                        <Linkedin className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">LinkedIn</p>
                          <a
                            href={c.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium hover:underline flex items-center gap-1"
                          >
                            Profile <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    )}
                    {c.portfolioUrl && (
                      <div className="flex items-center gap-3">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Portfolio</p>
                          <a
                            href={c.portfolioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium hover:underline flex items-center gap-1"
                          >
                            Website <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Skills */}
              {c.skills && c.skills.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Skills</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {c.skills.map((skill: string) => (
                        <Badge key={skill} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {c.currentTitle && (
                    <div>
                      <p className="text-sm text-muted-foreground">Current Title</p>
                      <p className="font-medium">{c.currentTitle}</p>
                    </div>
                  )}
                  {c.currentCompany && (
                    <div>
                      <p className="text-sm text-muted-foreground">Current Company</p>
                      <p className="font-medium">{c.currentCompany}</p>
                    </div>
                  )}
                  {c.experienceYears != null && (
                    <div>
                      <p className="text-sm text-muted-foreground">Experience</p>
                      <p className="font-medium">{c.experienceYears} years</p>
                    </div>
                  )}
                  {c.source && (
                    <div>
                      <p className="text-sm text-muted-foreground">Source</p>
                      <Badge variant="outline">{c.source}</Badge>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Added</p>
                    <p className="font-medium">{formatDate(c.createdAt)}</p>
                  </div>
                </CardContent>
              </Card>

              {c.tags && c.tags.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Tags</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1">
                      {c.tags.map((tag: string) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications" className="space-y-4">
          {c.applications && c.applications.length > 0 ? (
            c.applications.map((app: any) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Link
                          to="/jobs/$jobId"
                          params={{ jobId: app.job?.id }}
                          className="font-semibold hover:underline"
                        >
                          {app.job?.title || 'Unknown Job'}
                        </Link>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          {app.currentStage && (
                            <Badge
                              style={{
                                backgroundColor: (stageColors[app.currentStage.type] || '#6b7280') + '20',
                                color: stageColors[app.currentStage.type] || '#6b7280',
                              }}
                            >
                              {app.currentStage.name}
                            </Badge>
                          )}
                          <span>Applied {formatRelativeTime(app.createdAt)}</span>
                        </div>
                      </div>
                      <Badge
                        variant={app.status === 'active' ? 'default' : app.status === 'hired' ? 'default' : 'secondary'}
                        className={app.status === 'hired' ? 'bg-success/10 text-success' : app.status === 'rejected' ? 'bg-destructive/10 text-destructive' : ''}
                      >
                        {app.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Briefcase className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-1">No applications yet</h3>
                <p className="text-muted-foreground">
                  This candidate hasn't applied to any jobs yet.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          {/* Add note */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Textarea
                  placeholder="Add a note about this candidate..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="min-h-[80px]"
                />
                <Button
                  size="icon"
                  className="flex-shrink-0 self-end"
                  disabled={!noteContent.trim() || addNote.isPending}
                  onClick={() => addNote.mutate(noteContent.trim())}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          {(timeline as any[])?.length > 0 ? (
            (timeline as any[]).map((item: any) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-4 w-4 text-muted-foreground mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">
                            {item.user?.name || 'System'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(item.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {item.content}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-1">No activity yet</h3>
                <p className="text-muted-foreground">
                  Add a note to start tracking activity.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Candidate</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {c.firstName} {c.lastName}? This action cannot be undone and will remove all associated applications and documents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteCandidate.mutate()}
              loading={deleteCandidate.isPending}
            >
              Delete Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <EditCandidateDialog
        candidate={c}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] })}
      />
      </div>
    </PageLayout>
  )
}

function EditCandidateDialog({
  candidate,
  open,
  onOpenChange,
  onSuccess,
}: {
  candidate: any
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}) {
  const [form, setForm] = useState({
    firstName: candidate.firstName || '',
    lastName: candidate.lastName || '',
    email: candidate.email || '',
    phone: candidate.phone || '',
    location: candidate.location || '',
    currentTitle: candidate.currentTitle || '',
    currentCompany: candidate.currentCompany || '',
    linkedinUrl: candidate.linkedinUrl || '',
    portfolioUrl: candidate.portfolioUrl || '',
  })

  const updateCandidate = useMutation({
    mutationFn: (data: typeof form) =>
      candidatesApi.update(candidate.id, {
        ...data,
        phone: data.phone || undefined,
        location: data.location || undefined,
        currentTitle: data.currentTitle || undefined,
        currentCompany: data.currentCompany || undefined,
        linkedinUrl: data.linkedinUrl || undefined,
        portfolioUrl: data.portfolioUrl || undefined,
      }),
    onSuccess: () => {
      onSuccess()
      onOpenChange(false)
      toast({ title: 'Candidate updated', variant: 'success' })
    },
    onError: (err: any) => {
      toast({ title: 'Failed to update', description: err.message, variant: 'destructive' })
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Candidate</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Current Title</Label>
              <Input
                value={form.currentTitle}
                onChange={(e) => setForm({ ...form, currentTitle: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Current Company</Label>
              <Input
                value={form.currentCompany}
                onChange={(e) => setForm({ ...form, currentCompany: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>LinkedIn URL</Label>
              <Input
                value={form.linkedinUrl}
                onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Portfolio URL</Label>
              <Input
                value={form.portfolioUrl}
                onChange={(e) => setForm({ ...form, portfolioUrl: e.target.value })}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => updateCandidate.mutate(form)}
            disabled={!form.email.includes('@') || !form.firstName.trim() || !form.lastName.trim() || updateCandidate.isPending}
            loading={updateCandidate.isPending}
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
